import type { FC } from 'react';
import { Button, Copy, LabelDate } from '../../../../components';
import { DeleteOutline } from '@mui/icons-material';
import clsx from 'clsx';

type LeadCardProps = {
  title: string;
  date: string;
  createdBy: string;
  contactsCount: number;
  status: 'Completed' | 'Pending' | 'Failed' | 'Draft';
  onResultsClick?: () => void;
  onCopyClick?: () => void;
  onDeleteClick?: () => void;
  isDraft?: boolean;
  onContinueClick?: () => void;
  taskId: string;
};

export const CallTaskCard: FC<LeadCardProps> = ({
  title,
  date,
  createdBy,
  contactsCount,
  status,
  onResultsClick,
  onCopyClick,
  onDeleteClick,
  isDraft,
  onContinueClick,
}) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 w-full space-y-3">
      <div>
        <h2 className="font-semibold text-2xl pb-2">{title || 'Task_Name'}</h2>
        <LabelDate date={date} showTime formatDate="dd/MMM/yyyy" />
      </div>

      <hr />

      <div className="text-sm space-y-2">
        <div className="flex justify-between">
          <span className="text-secondary text-tiny">Created by</span>
          <span className="font-medium text-tiny">{createdBy}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-secondary text-tiny">Contacts</span>
          <button className="text-main hover:underline text-small">
            View all ({contactsCount})
          </button>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-secondary text-tiny">Status</span>
          <span
            className={clsx(
              'text-tiny px-2 py-0.5 rounded-full border',
              status === 'Completed'
                ? 'text-green-600 border-green-600 bg-green-50'
                : status === 'Pending'
                  ? 'text-yellow-600 border-yellow-600 bg-yellow-50'
                  : status === 'Draft'
                    ? 'text-blue-600 border-blue-600 bg-blue-50'
                    : 'text-red-600 border-red-600 bg-red-50'
            )}
          >
            {status}
          </span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-stretch pt-2 gap-2 w-full">
        {isDraft ? (
          <Button
            size="medium"
            label="Draft"
            onClick={onContinueClick}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full md:flex-1 md:min-w-0"
          />
        ) : (
          <Button
            size="medium"
            label="Results"
            onClick={onResultsClick}
            className="bg-purple-600 hover:bg-purple-700 text-white w-full md:flex-1 md:min-w-0"
          />
        )}

        <div className="flex gap-2 w-full md:w-auto md:shrink-0">
          <Button
            size="medium"
            type="empty"
            onClick={onCopyClick}
            className="shrink min-w-0 flex-1 lg:flex-none rounded-md border bg-gray-100 border-gray-300 hover:bg-gray-100"
            icon={<Copy />}
          />
          <Button
            size="medium"
            type="empty"
            onClick={onDeleteClick}
            className="shrink min-w-0 flex-1 lg:flex-none rounded-md border bg-red-50 border-gray-300 hover:bg-gray-100"
            icon={<DeleteOutline color="error" />}
          />
        </div>
      </div>
    </div>
  );
};
